import type React from 'react'
import {useState, useEffect} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {setSecurities} from '@/store/onboardingSlices'
import {getBuildTitle} from '@/components/SelfOnboarding/util/getLabel'
import CardWrapper from '@/components/GDS/molecules/CardWrapper/Card'
import {AutoCompleteWrapper} from '@/components/GDS/molecules/AutoCompleteWrapper'
import sharedStyles from '@/components/SelfOnboarding/Shared/OnboardingFormShared.module.css'
import {SECURITY_WIDGET} from '@/components/SelfOnboarding/util/constants'
import {Feedback, Label} from 'pepsico-ds'
import {anyWidgetFieldTouched, hasEmptyRequiredFields} from '@/components/SelfOnboarding/util/widgetErrorUtils'
import type {RootState} from '@/store'
import CustomInput from '@/components/common/CustomInputWrapper'

const initialSecurity = {
  staticAnalysisJob: '',
  sonarBranch: '',
  sJob: '',
}

type Suggestion = {id: string; label: string; fullObject: any}
const SecurityWidget: React.FC<{onValidationChange?: (hasError: boolean) => void}> = ({onValidationChange}) => {
  const security = useSelector((state: any) =>
    Array.isArray(state.security) && state.security.length > 0 ? state.security[0] : {...initialSecurity},
  )
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [touched, setTouched] = useState<{[key: string]: {touched?: boolean}}>({})
  const dispatch = useDispatch()
  const isDark = useSelector((state: RootState) => state.theme.mode === 'dark')

  useEffect(() => {
    if (!security || Object.keys(security).length === 0) {
      dispatch(setSecurities([{...initialSecurity}]))
    }
  }, [security, dispatch])

  const updateSecurity = (field: string, value: string) => {
    dispatch(setSecurities([{...security, [field]: value}]))
    // If the field was already touched, trigger validation by updating touched state
    if (touched[field]) {
      setTouched(prev => ({...prev, [field]: {touched: true}}))
    }
  }
  const handleBlur = (field: string) => {
    setTouched(prev => ({
      ...prev,
      [field]: {touched: true},
    }))
  }

  useEffect(() => {
    const anyTouched = anyWidgetFieldTouched(touched)
    const hasError = anyTouched && hasEmptyRequiredFields(['staticAnalysisJob'], [security])
    if (onValidationChange) onValidationChange(!!hasError)
  }, [security, touched, onValidationChange])

  const widgetTouched = anyWidgetFieldTouched(touched)
  // Utility to check if staticAnalysisJob is empty (handles string or object)
  const isStaticAnalysisJobEmpty = (val: any) => {
    if (!val) return true
    if (typeof val === 'string') return val.trim() === ''
    if (typeof val === 'object') {
      return !val.label && !val.description
    }
    return false
  }

  return (
    <CardWrapper
      cardHeader={{
        title: SECURITY_WIDGET.staticAnalysisLabel,
        rightAccessory: {
          iconButtons: [
            {
              icon: 'delete_outline',
              onClick: () => {
                dispatch(setSecurities([{...initialSecurity}]))
                setTouched({})
              },
            },
          ],
        },
      }}
      cardBody={{type: 'slot', slot: <></>}}
      state="non-interactive"
      type="basic"
    >
      <form className="flex w-full flex-col items-start">
        <div className="flex w-full items-start gap-4">
          <div className="flex w-[282px] flex-col items-start">
            <Label className={sharedStyles.autocompleteLabelFont} label={SECURITY_WIDGET.staticAnalysisLabel} />
            <AutoCompleteWrapper
              icon="keyboard_arrow_down"
              limitSuggestions={30}
              placeholderText={SECURITY_WIDGET.staticAnalysisPlaceholder}
              onClear={() => updateSecurity('staticAnalysisJob', '')}
              suggestions={suggestions}
              isAutoComplete={true}
              isLeadingIcon={false}
              searchTerm={
                typeof security.staticAnalysisJob === 'string'
                  ? security.staticAnalysisJob
                  : security.staticAnalysisJob && typeof security.staticAnalysisJob === 'object'
                    ? 'description' in security.staticAnalysisJob
                      ? security.staticAnalysisJob.description
                      : 'label' in security.staticAnalysisJob
                        ? security.staticAnalysisJob.label
                        : ''
                    : ''
              }
              onUpdate={async (query: string) => {
                setLoading(true)
                setError('')
                try {
                  const {searchItemsBySearchField} = await import('@/services/team/teamService')
                  const results = await searchItemsBySearchField('codequality', query)
                  const mapped = Array.isArray(results)
                    ? results
                        .map((item: any) => ({
                          id: item.id || item._id || item.value || item.label,
                          label: getBuildTitle(item),
                          fullObject: item,
                        }))
                        .filter(option => option.label && typeof option.label === 'string')
                    : []
                  setSuggestions(mapped)
                  setLoading(false)
                } catch (err) {
                  setLoading(false)
                  setError('Failed to load suggestions')
                }
              }}
              onSelectOption={(option: any) => updateSecurity('staticAnalysisJob', option)}
              onBlur={() => handleBlur('staticAnalysisJob')}
            />
            <Feedback
              variant="info"
              className={`${sharedStyles.infoLabel} ${sharedStyles.autocompleteLabelFont}`}
              message={SECURITY_WIDGET.sonarProjectIdLabel}
            />
            {widgetTouched && isStaticAnalysisJobEmpty(security.staticAnalysisJob) && (
              <div className="mt-1 text-xs text-red-500">Required</div>
            )}
          </div>
          <div className="flex w-[282px] flex-col items-start">
            <CustomInput
              placeholder={SECURITY_WIDGET.placeholder}
              label={SECURITY_WIDGET.label}
              name="sonarBranch"
              value={security.sonarBranch}
              onBlur={() => handleBlur('sonarBranch')}
              onChange={e => updateSecurity('sonarBranch', e.target.value)}
              helperText={SECURITY_WIDGET.helper}
            />
          </div>
        </div>
      </form>
    </CardWrapper>
  )
}

export default SecurityWidget
